import { useState, useMemo } from 'react';
import { actMathWorksheets } from '../worksheets/worksheets-act-math';

const domains = [
  'All Domains',
  'Pre-Algebra',
  'Elementary Algebra',
  'Intermediate Algebra',
  'Coordinate Geometry',
  'Plane Geometry',
  'Trigonometry'
];

const domainColors: Record<string, string> = {
  'Pre-Algebra': 'bg-blue-100 text-blue-700 border-blue-200',
  'Elementary Algebra': 'bg-green-100 text-green-700 border-green-200',
  'Intermediate Algebra': 'bg-purple-100 text-purple-700 border-purple-200',
  'Coordinate Geometry': 'bg-orange-100 text-orange-700 border-orange-200',
  'Plane Geometry': 'bg-pink-100 text-pink-700 border-pink-200',
  'Trigonometry': 'bg-teal-100 text-teal-700 border-teal-200'
};

export default function ACTMathWorksheetsPage() {
  const [selectedDomain, setSelectedDomain] = useState('All Domains');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter worksheets
  const filteredWorksheets = useMemo(() => {
    return actMathWorksheets.filter((worksheet) => {
      const matchesDomain = selectedDomain === 'All Domains' || worksheet.topic === selectedDomain;
      const matchesSearch = searchQuery === '' || 
        worksheet.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        worksheet.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesDomain && matchesSearch;
    });
  }, [selectedDomain, searchQuery]);

  // Count worksheets per domain
  const domainCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    domains.forEach(domain => {
      if (domain === 'All Domains') {
        counts[domain] = actMathWorksheets.length;
      } else {
        counts[domain] = actMathWorksheets.filter(w => w.topic === domain).length;
      }
    });
    return counts;
  }, []);

  // Separate full review packs from topic-specific drills
  const fullReviewPacks = filteredWorksheets.filter(w => w.title.includes('Full Review'));
  const topicDrills = filteredWorksheets.filter(w => !w.title.includes('Full Review'));

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-violet-600 to-purple-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
              <i className="ri-file-list-3-line mr-2"></i>
              66 Topic-Specific Drills
            </div>
            <h1 className="text-5xl font-bold mb-6">ACT Math Worksheets</h1>
            <p className="text-xl mb-8 text-violet-50 max-w-3xl mx-auto">
              Browse all ACT Math practice worksheets organized by domain. Download and print topic-specific drills to master every concept tested on the ACT.
            </p>
            <div className="flex items-center justify-center gap-8 text-lg">
              <div className="flex items-center gap-2">
                <i className="ri-file-text-line text-2xl"></i>
                <span className="font-semibold">60 Topic Drills</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-book-open-line text-2xl"></i>
                <span className="font-semibold">6 Full Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-printer-line text-2xl"></i>
                <span className="font-semibold">Print Ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Banner */}
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 border-b border-violet-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">Quick Access:</span>
            <a
              href="/act-math-study-plan"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-schedule-line"></i>
              Create Study Plan
            </a>
            <a
              href="/test-prep/act/math"
              className="flex items-center gap-2 px-4 py-2 bg-white text-violet-600 border-2 border-violet-600 rounded-lg font-semibold text-sm hover:bg-violet-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              View Full Curriculum
            </a>
          </div>
        </div>
      </div>

      {/* Domain Filter Tabs */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                placeholder="Search ACT Math worksheets by topic or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Domain Filter Buttons */}
          <div className="flex flex-wrap gap-3 justify-center">
            {domains.map((domain) => (
              <button
                key={domain}
                onClick={() => setSelectedDomain(domain)}
                className={`px-5 py-2 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  selectedDomain === domain
                    ? 'bg-violet-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {domain}
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {domainCounts[domain]}
                </span>
              </button>
            ))}
          </div>

          {/* Results Count */}
          <div className="mt-4 text-center text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredWorksheets.length}</span> worksheets
            {selectedDomain !== 'All Domains' && (
              <button
                onClick={() => setSelectedDomain('All Domains')}
                className="ml-4 text-violet-600 hover:text-violet-700 font-medium whitespace-nowrap cursor-pointer"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Full Review Packs Section */}
      {fullReviewPacks.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-violet-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-8">
              <div className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-3 whitespace-nowrap">
                <i className="ri-star-line mr-2"></i>
                Comprehensive Review
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Full Domain Review Packs</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Complete review worksheets covering all topics within each domain. Perfect for comprehensive practice before test day.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {fullReviewPacks.map((worksheet) => (
                <div
                  key={worksheet.id}
                  className="bg-white rounded-lg border-2 border-violet-200 overflow-hidden hover:shadow-xl transition-all group"
                >
                  {/* Thumbnail */}
                  <div className="relative w-full h-64 bg-gray-100 overflow-hidden">
                    <img
                      src={worksheet.thumbnailUrl}
                      alt={worksheet.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Full Review Badge */}
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 bg-violet-600 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-star-fill"></i>
                        FULL REVIEW
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    {/* Domain Tag */}
                    <div className="mb-3">
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full border whitespace-nowrap ${domainColors[worksheet.topic]}`}>
                        {worksheet.topic}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {worksheet.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4">
                      {worksheet.description}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-sm text-gray-500 flex items-center gap-1 font-semibold">
                        <i className="ri-file-text-line"></i>
                        {worksheet.pages} pages
                      </span>
                      <button className="px-5 py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                        <i className="ri-download-line"></i>
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Topic-Specific Drills Section */}
      {topicDrills.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                {selectedDomain === 'All Domains' ? 'Topic-Specific Drills' : `${selectedDomain} Drills`}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Focused practice worksheets targeting individual skills and concepts. Perfect for targeted review and skill building.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {topicDrills.map((worksheet) => (
                <div
                  key={worksheet.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  {/* Thumbnail */}
                  <div className="relative w-full h-56 bg-gray-100 overflow-hidden">
                    <img
                      src={worksheet.thumbnailUrl}
                      alt={worksheet.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Difficulty Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full whitespace-nowrap">
                        {worksheet.difficulty}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Domain Tag */}
                    <div className="mb-2">
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded border whitespace-nowrap ${domainColors[worksheet.topic]}`}>
                        {worksheet.topic}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2">
                      {worksheet.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {worksheet.description}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <i className="ri-file-text-line"></i>
                        {worksheet.pages} pages
                      </span>
                      <button className="px-4 py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                        <i className="ri-download-line"></i>
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* No Results */}
      {filteredWorksheets.length === 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No worksheets found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filter</p>
            <button
              onClick={() => {
                setSelectedDomain('All Domains');
                setSearchQuery('');
              }}
              className="px-6 py-3 bg-violet-600 text-white rounded-lg font-semibold hover:bg-violet-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear All Filters
            </button>
          </div>
        </section>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <a
          href="/test-prep/act/math"
          className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
            <i className="ri-book-open-line text-2xl text-blue-600"></i>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">ACT Math Curriculum</h3>
          <p className="text-sm text-slate-600">Review all topics and practice questions</p>
        </a>

        <a
          href="/act-math-study-plan"
          className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
        >
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
            <i className="ri-calendar-check-line text-2xl text-green-600"></i>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Study Plan</h3>
          <p className="text-sm text-slate-600">Get a weekly prep schedule for test day</p>
        </a>

        <a
          href="/act-math-progress"
          className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
        >
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
            <i className="ri-checkbox-circle-line text-2xl text-purple-600"></i>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Track Progress</h3>
          <p className="text-sm text-slate-600">Mark worksheets complete and see stats</p>
        </a>
      </div>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-violet-600 to-purple-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <i className="ri-trophy-line text-5xl mb-4"></i>
          <h2 className="text-3xl font-bold mb-4">Ready to Master the ACT Math Section?</h2>
          <p className="text-lg text-violet-50 mb-8">
            Get unlimited access to all 66 ACT Math worksheets, plus personalized tutoring, study plans, and progress tracking to help you achieve your target score.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="/test-prep/act/math"
              className="inline-block px-8 py-4 bg-white text-violet-600 font-bold text-lg rounded-lg hover:bg-violet-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              View Full Curriculum
            </a>
            <a
              href="#pricing"
              className="inline-block px-8 py-4 bg-violet-700 text-white font-bold text-lg rounded-lg hover:bg-violet-800 transition-colors border-2 border-white/30 whitespace-nowrap cursor-pointer"
            >
              Get Started
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}